import React from 'react'
import { useApp } from '../context/AppContext'
import { Business, ClientSummary, ConflictError, Service, ValidationError, api } from '../api/client'
import { newAppointmentId } from '../api/ids'
import { bookedMessage, conflictMessage } from '../calendar/booking'
import { clientName } from '../calendar/names'
import { isCurrentRequest } from '../clients/list'
import { displayPhone } from '../clients/phone'
import { formatPrice, priceOrFree, formatLength } from '../catalog/format'
import { lengthSummary } from '../screens/settings/serviceForm'
import { isRedeemable } from '../packages/display'
import { depositToAsk } from '../payments/deposit'
import type { Discount, Purchase } from '../types/app'
import { saleFor } from './discount'
import Field from '../screens/settings/Field'
import { saveErrorMessage, showToast } from '../screens/settings/toast'
import { useDialog } from '../hooks/useDialog'
import TimePicker from './TimePicker'
import LengthPicker from './LengthPicker'
import NewClientForm from './NewClientForm'
import './Sheet.css'
import './Booking.css'
import '../screens/settings/Settings.css'

// localToday is the local calendar date, YYYY-MM-DD: the date a sale is judged on.
function localToday(): string {
  const n = new Date()
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`
}

export default function BookingSheet() {
  const { state, dispatch } = useApp()
  // One id for the sheet's lifetime, so a retried or forced save can't book twice.
  const [id] = React.useState(() => newAppointmentId())

  const [query, setQuery] = React.useState('')
  const [clients, setClients] = React.useState<ClientSummary[] | null>(null)
  const [client, setClient] = React.useState<ClientSummary | null>(state.booking?.client ?? null)
  const [purchases, setPurchases] = React.useState<Purchase[] | null>(null)
  const purchasesToken = React.useRef(0)
  const [adding, setAdding] = React.useState(false)
  const [addingName, setAddingName] = React.useState('')
  const [savingClient, setSavingClient] = React.useState(false)

  const [services, setServices] = React.useState<Service[] | null>(null)
  const [serviceId, setServiceId] = React.useState<string | null>(null)
  // The length booked. A one-length service has it at once; with several,
  // nothing is chosen until she picks one (spec §4.3).
  const [lengthId, setLengthId] = React.useState<string | null>(null)
  const [date, setDate] = React.useState(state.booking?.date ?? state.calendarDate)
  const [start, setStart] = React.useState<number | null>(state.booking?.startMinute ?? null)

  const [errors, setErrors] = React.useState<Record<string, string>>({})
  const [conflict, setConflict] = React.useState<string | null>(null)
  const [error, setError] = React.useState('')
  const [saving, setSaving] = React.useState(false)

  const close = () => dispatch({ type: 'CLOSE_SHEET' })
  const dialogRef = React.useRef<HTMLDivElement>(null)
  useDialog(dialogRef, close, { closable: !saving && !savingClient })

  React.useEffect(() => {
    api
      .listServices()
      .then(setServices)
      .catch(() => setError("Couldn’t load your treatments. Check your connection and try again."))
  }, [])

  // Package credits shown under the chosen client's name are information
  // only — they don't affect how this booking gets paid; that choice is
  // made later, when the appointment is completed.
  const clientId = client?.id ?? null
  React.useEffect(() => {
    setPurchases(null)
    if (!clientId) {
      return
    }
    const token = ++purchasesToken.current
    api
      .clientPackages(clientId)
      .then((list) => {
        if (isCurrentRequest(token, purchasesToken.current)) setPurchases(list)
      })
      .catch(() => {
        if (isCurrentRequest(token, purchasesToken.current)) setPurchases(null)
      })
  }, [clientId])
  // Deposit link (Jessica's feedback): when she takes deposits online and the
  // client has an email, the booking offers to email them a deposit link,
  // ticked by default. The email address is looked up for the chosen client.
  const [business, setBusiness] = React.useState<Business | null>(null)
  const [clientEmail, setClientEmail] = React.useState<string | null>(null)
  const [askDeposit, setAskDeposit] = React.useState(true)
  React.useEffect(() => {
    api.getBusiness().then(setBusiness).catch(() => {})
  }, [])
  React.useEffect(() => {
    setClientEmail(null)
    if (!clientId) return
    let live = true
    api.getClient(clientId).then((c) => { if (live) setClientEmail(c.email) }).catch(() => {})
    return () => { live = false }
  }, [clientId])
  // A live sale applies by itself. Showing it here with a ✕ lets her book at
  // full price instead (no_sale), as the server would otherwise apply it.
  const [offers, setOffers] = React.useState<Discount[]>([])
  const [noSale, setNoSale] = React.useState(false)
  React.useEffect(() => {
    api.listDiscounts().then(setOffers).catch(() => {})
  }, [])
  React.useEffect(() => setNoSale(false), [serviceId, lengthId])
  const chosenService = services?.find((s) => s.id === serviceId) ?? null
  const chosenLength = chosenService?.lengths.find((l) => l.id === lengthId) ?? null
  const sale = chosenLength && !noSale ? saleFor(chosenLength, offers, localToday()) : null
  const depositAmount = clientEmail && chosenLength ? depositToAsk(business, chosenLength.price_pence - (sale?.pence ?? 0)) : 0

  const remainingCredits = purchases?.filter((p) => isRedeemable(p)).reduce((sum, p) => sum + p.remaining, 0) ?? 0

  React.useEffect(() => {
    if (client || adding) {
      return
    }
    let active = true
    const timer = setTimeout(() => {
      api
        .searchClients(query)
        .then((list) => active && setClients(list))
        .catch((e) => active && setError(saveErrorMessage(e)))
    }, 250)
    return () => {
      active = false
      clearTimeout(timer)
    }
  }, [query, client, adding])

  const step: 'client' | 'service' | 'time' = !client ? 'client' : !serviceId ? 'service' : 'time'

  const save = async (force: boolean) => {
    const missing: Record<string, string> = {}
    if (!client) missing.client_id = 'Choose a client.'
    if (!serviceId) missing.service_id = 'Choose a treatment.'
    else if (!lengthId) missing.service_length_id = 'Choose a length.'
    if (start === null) missing.start_minute = 'Choose a start time.'
    if (!client || !serviceId || !lengthId || start === null) {
      setErrors(missing)
      return
    }
    setSaving(true)
    setErrors({})
    setError('')
    setConflict(null)
    try {
      const appointment = await api.createAppointment({
        id,
        client_id: client.id,
        service_length_id: lengthId,
        date,
        start_minute: start,
        force,
        ...(noSale ? { no_sale: true } : {}),
      })
      let message = bookedMessage(appointment)
      if (depositAmount > 0 && askDeposit) {
        try {
          await api.requestDeposit(appointment.id)
          message += ` · deposit link emailed to ${client.first_name}`
        } catch {
          message += " · the deposit link didn’t send. Send it again from the appointment."
        }
      }
      close()
      dispatch({ type: 'SET_CALENDAR_DATE', payload: appointment.date })
      dispatch({ type: 'APPOINTMENTS_CHANGED' })
      showToast(dispatch, message)
    } catch (e) {
      if (e instanceof ConflictError) {
        setConflict(conflictMessage(e.conflicts, { date, start_minute: start }, e.blocks))
      } else if (e instanceof ValidationError) {
        setErrors(e.fields)
      } else {
        setError(saveErrorMessage(e))
      }
      setSaving(false)
    }
  }

  // .settings-sheet: from 1024px the side rail's New booking button stays in
  // reach while Settings is open, so this sheet must cover Settings too.
  return (
    <div className="sheet-overlay settings-sheet" onClick={() => { if (!saving && !savingClient) close() }}>
      <div ref={dialogRef} className="sheet-content" role="dialog" aria-modal="true" aria-label="New booking" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-handle"></div>
        {/* One step at a time (design review P8): client, then service, then
            time; the step is the eyebrow above the title. What's chosen so
            far stays below as a summary with Change. */}
        <div className="sheet-header">
          <p className="booking-step">{step === 'client' ? 'Step 1 of 3 · Client' : step === 'service' ? 'Step 2 of 3 · Treatment' : 'Step 3 of 3 · Time'}</p>
          <h2>New booking</h2>
        </div>

        <div className="sheet-body booking-form">
          {step !== 'client' && client && (
            <div className="booking-chosen">
              <span><span className="booking-chosen__label">Client</span> {clientName(client)}
                {remainingCredits > 0 && <span className="booking-option__meta"> · {remainingCredits} package {remainingCredits === 1 ? 'session' : 'sessions'} left</span>}
              </span>
              <button type="button" className="btn btn-quiet btn-compact" onClick={() => { setClient(null); setQuery('') }}>Change</button>
            </div>
          )}
          {step === 'time' && chosenService && (
            <div className="booking-chosen">
              <span><span className="booking-chosen__label">Treatment</span>{' '}
                {chosenLength
                  ? `${chosenService.name} · ${formatLength(chosenLength.duration_minutes)} · ${priceOrFree(chosenLength.price_pence)}`
                  : chosenService.name}
              </span>
              <button type="button" className="btn btn-quiet btn-compact" onClick={() => { setServiceId(null); setLengthId(null); setConflict(null) }}>Change</button>
            </div>
          )}
          {step === 'time' && chosenService && chosenService.lengths.length > 1 && (
            <div className="booking-section">
              <LengthPicker
                lengths={chosenService.lengths}
                selected={lengthId}
                onSelect={(l) => {
                  setLengthId(l.id)
                  setConflict(null)
                }}
              />
            </div>
          )}
          {step === 'time' && errors.service_length_id && !lengthId && (
            <p className="field-error" role="alert">{errors.service_length_id}</p>
          )}

          {step === 'client' && (
          <section className="booking-section" aria-labelledby="booking-client-label">
            <h3 id="booking-client-label" className="booking-label">Client</h3>
            {client ? (
              <div className="booking-option" aria-pressed="true">
                <span className="booking-option__title">{clientName(client)}</span>
                {remainingCredits > 0 && (
                  <span className="booking-option__meta">
                    {remainingCredits} package {remainingCredits === 1 ? 'credit' : 'credits'} remaining
                  </span>
                )}
                {client.phone && <span className="booking-option__meta">{displayPhone(client.phone)}</span>}
                <button type="button" className="btn btn-quiet btn-compact" onClick={() => { setClient(null); setQuery('') }}>
                  Change client
                </button>
              </div>
            ) : adding ? (
              <NewClientForm
                firstName={addingName}
                cancelLabel="Back to search"
                onBusyChange={setSavingClient}
                onCancel={() => setAdding(false)}
                onCreated={(created) => {
                  setClient(created)
                  setAdding(false)
                }}
              />
            ) : (
              <>
                <input
                  type="search"
                  aria-label="Search clients"
                  placeholder="Search by name or phone"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
                {clients !== null && clients.length > 0 && (
                  <div className="booking-options">
                    {clients.map((c) => (
                      <button key={c.id} type="button" className="booking-option" onClick={() => setClient(c)}>
                        <span className="booking-option__title">{clientName(c)}</span>
                        {c.phone && <span className="booking-option__meta">{displayPhone(c.phone)}</span>}
                      </button>
                    ))}
                  </div>
                )}
                {clients !== null && clients.length === 0 && (
                  <p className="field-hint">{query.trim() ? 'No clients match.' : 'No clients yet.'}</p>
                )}
                <button
                  type="button"
                  className="btn btn-quiet btn-compact"
                  onClick={() => {
                    setAddingName(query.trim())
                    setAdding(true)
                  }}
                >
                  New client
                </button>
              </>
            )}
            {errors.client_id && <p className="field-error" role="alert">{errors.client_id}</p>}
          </section>
          )}

          {step === 'service' && (
          <section className="booking-section" aria-labelledby="booking-service-label">
            <h3 id="booking-service-label" className="booking-label">Treatment</h3>
            {services === null ? (
              <p className="field-hint">Loading treatments…</p>
            ) : services.length === 0 ? (
              <p className="field-hint">Add a treatment in Settings → Menu first.</p>
            ) : (
              <div className="booking-options">
                {services.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    className="booking-option"
                    aria-pressed={serviceId === s.id}
                    onClick={() => {
                      setServiceId(s.id)
                      setLengthId(s.lengths.length === 1 ? s.lengths[0].id : null)
                      setConflict(null)
                    }}
                  >
                    <span className="booking-option__title">
                      <span className="category-dot" style={{ background: s.colour }} aria-hidden="true" />
                      {s.name}
                    </span>
                    <span className="booking-option__meta">
                      {lengthSummary(s)}
                    </span>
                  </button>
                ))}
              </div>
            )}
            {errors.service_id && <p className="field-error" role="alert">{errors.service_id}</p>}
          </section>
          )}

          {step === 'time' && (
          <section aria-labelledby="booking-time-label">
            <h3 id="booking-time-label" className="booking-label">Time</h3>
            <TimePicker
              serviceLengthId={lengthId}
              emptyHint="Choose a length to see free times."
              date={date}
              start={start}
              error={errors.start_minute ?? errors.date}
              onDateChange={(d) => {
                setDate(d)
                setConflict(null)
              }}
              onStartChange={(m) => {
                setStart(m)
                setConflict(null)
              }}
            />
          </section>
          )}

          {step === 'time' && sale && (
            <div className="booking-chosen">
              <span><span className="booking-chosen__label">Discount</span> {sale.offer.name} −{formatPrice(sale.pence)}</span>
              <button type="button" className="btn btn-quiet btn-compact" aria-label={`Remove ${sale.offer.name}`} onClick={() => setNoSale(true)}>✕</button>
            </div>
          )}

          {step === 'time' && depositAmount > 0 && (
            <label className="checkbox-row">
              <input type="checkbox" checked={askDeposit} onChange={(e) => setAskDeposit(e.target.checked)} />
              <span>Email {client?.first_name} a {formatPrice(depositAmount)} deposit link</span>
            </label>
          )}

          {conflict && (
            <div className="booking-conflict" role="alert">
              <p>{conflict}</p>
              <button type="button" className="btn btn-secondary btn-compact" onClick={() => save(true)} disabled={saving}>
                Book anyway
              </button>
            </div>
          )}
          {errors.id && <p className="field-error" role="alert">{errors.id}</p>}
          {error && <p className="field-error" role="alert">{error}</p>}
        </div>

        <div className={step === 'time' ? 'sheet-actions' : 'sheet-actions sheet-actions--single'}>
          <button type="button" className="btn btn-secondary" onClick={close} disabled={saving || savingClient}>
            Cancel
          </button>
          {step === 'time' && (
            <button type="button" className="btn btn-primary" onClick={() => save(false)} disabled={saving}>
              {saving ? 'Booking…' : 'Book'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
